import type { ReactNode } from 'react';
import { cloneElement, createContext, isValidElement, useContext } from 'react';
import { RadioGroup, Radio } from 'react-aria-components';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { type IconProps } from '../icons/icon';

type ChoiceGroupProps = {
  /** Accessible name for the group, e.g. "Select a location". */
  'aria-label': string;
  /** Selected value (controlled). */
  value?: string;
  /** Uncontrolled initial value. */
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** When true (default), an interactive radio group; the consumer keeps one
   *  item selected via `value`/`defaultValue`. When false, static
   *  non-interactive chips with no selected styling and no radiogroup
   *  semantics. */
  interactive?: boolean;
  children: ReactNode;
  className?: string;
};

type ChoiceProps = {
  value: string;
  /** Optional leading icon (e.g. <LocationIcon/>). */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
};

const ChoiceGroupContext = createContext<{ interactive: boolean }>({
  interactive: true,
});

/**
 * A single-select group of pill-shaped options. Interactive by default (React
 * Aria RadioGroup, one item always selected); pass `interactive={false}` to
 * render static, non-interactive chips (e.g. a lone location where there's no
 * choice to make). Pair with `<Choice>` children.
 */
export function ChoiceGroup({
  'aria-label': ariaLabel,
  value,
  defaultValue,
  onChange,
  interactive = true,
  children,
  className,
}: ChoiceGroupProps) {
  if (!interactive) {
    return (
      <ChoiceGroupContext.Provider value={{ interactive: false }}>
        <div className={cx(rowStyles, className)}>{children}</div>
      </ChoiceGroupContext.Provider>
    );
  }
  return (
    <ChoiceGroupContext.Provider value={{ interactive: true }}>
      <RadioGroup
        aria-label={ariaLabel}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        orientation="horizontal"
        className={cx(rowStyles, className)}
      >
        {children}
      </RadioGroup>
    </ChoiceGroupContext.Provider>
  );
}

/** One option in a `<ChoiceGroup>`. Renders a pill-styled radio when the group
 *  is interactive, else a static chip. */
export function Choice({ value, icon, children, className }: ChoiceProps) {
  const { interactive } = useContext(ChoiceGroupContext);

  if (!interactive) {
    return (
      <span className={cx(staticStyles, className)}>
        {isValidElement<IconProps>(icon) &&
          cloneElement(icon, { size: 'medium' })}
        <Text style="label-small" color="secondary" className={textStyles}>
          {children}
        </Text>
      </span>
    );
  }

  return (
    <Radio
      value={value}
      className={({ isSelected }) =>
        cx(
          isSelected ? interactiveSelectedStyles : interactiveStyles,
          className
        )
      }
    >
      {({ isSelected }) => (
        <>
          {isValidElement<IconProps>(icon) &&
          cloneElement(icon, { size: 'medium' })}
          <Text
            style="label-small"
            color={isSelected ? 'primary' : 'secondary'}
            className={textStyles}
          >
            {children}
          </Text>
        </>
      )}
    </Radio>
  );
}

// Horizontal, wrapping row of chips — matches the old LocationSection chip row.
const rowStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'small',
});

// Layout shared by all chip states (ported verbatim from the deleted `Pill`).
const pillBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'tiny',
  maxWidth: '[300px]',
  paddingX: 'small',
  borderRadius: 'small',
  border: 'small',
} as const;

// Static (non-interactive) chip.
const staticStyles = css({
  ...pillBase,
  borderColor: 'border.neutral',
  color: 'content.secondary',
});

// Interactive, unselected.
const interactiveStyles = css({
  ...pillBase,
  borderColor: 'border.neutral',
  color: 'content.secondary',
  cursor: 'interactive',
  _hover: {
    borderColor: 'border.interactive',
    color: 'content.primary',
  },
  '&[data-focus-visible]': {
    outline: 'medium',
    outlineColor: 'border.focus',
    borderColor: 'border.focus',
  },
});

// Interactive, selected.
const interactiveSelectedStyles = css({
  ...pillBase,
  borderColor: 'content.primary',
  outlineColor: 'content.primary',
  color: 'content.primary',
  cursor: 'interactive',
  outline: 'small',
  '&[data-focus-visible]': {
    outline: 'medium',
    outlineColor: 'border.focus',
    borderColor: 'border.focus',
  },
});

const textStyles = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: '[0]',
  paddingY: "xsmall"
});
